import { action, observable } from 'mobx';
import { BusyStore } from 'ui/base/button';
import { BooleanStore } from 'ui/base/input';
import { Host, IPangolinDomain } from 'services/pangolin/types';
import { Optional, ResultKind } from 'services/base/types';
import { HostsService, HostsUpdateResult } from 'services/pangolin/hosts_service';

export class ViewDeviceStore {
  host: Host;

  blockInternet: Optional<BooleanStore>;
  blockInternal: Optional<BooleanStore>;
  ignoreSchedules: Optional<BooleanStore>;
  ignoreAll: Optional<BooleanStore>;
  notifyOnUp: Optional<BooleanStore>;
  blockAdTracking: Optional<BooleanStore>;
  blockMalicious: Optional<BooleanStore>;
  masquerade: Optional<BooleanStore>;

  @observable.ref
  busyBlockInternet: BusyStore;

  @observable.ref
  busyBlockInternal: BusyStore;

  @observable.ref
  busyIgnoreSchedules: BusyStore;

  @observable.ref
  busyIgnoreAll: BusyStore;

  @observable.ref
  busyNotifyOnUp: BusyStore;

  @observable.ref
  busyBlockAdTracking: BusyStore;

  @observable.ref
  busyBlockMalicious: BusyStore;

  @observable.ref
  busyMasquerade: BusyStore;

  constructor(host: Host) {
    this.host = host;
    this.blockInternet = host.blockInternet !== undefined ? new BooleanStore(host.blockInternet) : undefined;
    this.blockInternal = host.blockInternal !== undefined ? new BooleanStore(host.blockInternal) : undefined;
    this.ignoreSchedules = host.ignoreSchedules !== undefined ? new BooleanStore(host.ignoreSchedules) : undefined;
    this.ignoreAll = host.ignoreAll !== undefined ? new BooleanStore(host.ignoreAll) : undefined;
    this.notifyOnUp = host.notifyOnUp !== undefined ? new BooleanStore(host.notifyOnUp) : undefined;
    this.blockAdTracking = host.blockAdTracking !== undefined ? new BooleanStore(host.blockAdTracking) : undefined;
    this.blockMalicious = host.blockMalicious !== undefined ? new BooleanStore(host.blockMalicious) : undefined;
    this.masquerade = host.masquerade !== undefined ? new BooleanStore(host.masquerade) : undefined;
    this.busyBlockInternet = new BusyStore();
    this.busyBlockInternal = new BusyStore();
    this.busyIgnoreSchedules = new BusyStore();
    this.busyIgnoreAll = new BusyStore();
    this.busyNotifyOnUp = new BusyStore();
    this.busyBlockAdTracking = new BusyStore();
    this.busyBlockMalicious = new BusyStore();
    this.busyMasquerade = new BusyStore();
  }

  @action.bound
  async handleSave(domain: IPangolinDomain, values: Partial<Host>): Promise<HostsUpdateResult> {
    if (domain.hosts.has(this.host.id)) {
      const result = await HostsService.update(domain, {
        id: this.host.id, ...values,
      });

      return result;
    }

    return { kind: ResultKind.SYSTEM_ERROR };
  }

  @action.bound
  handleBlockInternetChange(domain: IPangolinDomain, value: boolean) {
    return this.busyBlockInternet.perform(() => this.handleSave(
      domain, { blockInternet: value })
    );
  }

  @action.bound
  handleBlockInternalChange(domain: IPangolinDomain, value: boolean) {
    return this.busyBlockInternal.perform(() => this.handleSave(
      domain, { blockInternal: value })
    );
  }

  @action.bound
  handleIgnoreSchedulesChange(domain: IPangolinDomain, value: boolean) {
    return this.busyIgnoreSchedules.perform(() => this.handleSave(
      domain, { ignoreSchedules: value })
    );
  }

  @action.bound
  handleIgnoreAllChange(domain: IPangolinDomain, value: boolean) {
    return this.busyIgnoreAll.perform(() => this.handleSave(
      domain, { ignoreAll: value })
    );
  }

  @action.bound
  handleNotifyOnUpChange(domain: IPangolinDomain, value: boolean) {
    return this.busyNotifyOnUp.perform(() => this.handleSave(
      domain, { notifyOnUp: value })
    );
  }

  @action.bound
  handleBlockAdTrackingChange(domain: IPangolinDomain, value: boolean) {
    return this.busyBlockAdTracking.perform(() => this.handleSave(
      domain, { blockAdTracking: value })
    );
  }

  @action.bound
  handleBlockMaliciousFeaturesChange(domain: IPangolinDomain, value: boolean) {
    return this.busyBlockMalicious.perform(() => this.handleSave(
      domain, { blockMalicious: value })
    );
  }

  @action.bound
  handleMasqueradeChange(domain: IPangolinDomain, value: boolean) {
    return this.busyMasquerade.perform(() => this.handleSave(
      domain, { masquerade: value })
    );
  }
}
